/*
src/data/SDImage.js
wgbh-skydiagram-testing
astro.unl.edu
2019-07-14
*/


import {CallbackMakerMixin} from '../utils/CallbackMakerMixin.js';

const uuidv4 = require('uuid/v4');


/*

Callbacks:
	change	- an image property or setting has changed; no argument

*/



export class SDImage {


	constructor(workspace, bank, initObj) {

		// Note: SDImageBank will access _id directly (read-only).

		this._workspace = workspace;
		this._bank = bank;

		this._retainCount = 0;

		if (typeof initObj === 'object') {
			if (initObj.hasOwnProperty('def')) {
				this._initWithDef(initObj.def);
			} else if (initObj.hasOwnProperty('file')) {
				this._initWithFile(initObj.file);
			} else if (initObj.hasOwnProperty('transaction') && initObj.hasOwnProperty('metadata')) {
				this._initWithTransaction(initObj.transaction, initObj.metadata);
			} else {
				this._init();
			}
		} else {
			this._init();
		}

		this._setDimensions = this._setDimensions.bind(this);

		CallbackMakerMixin.apply(this);
	}

	_initWithTransaction(t, metaObj) {

		if (typeof metaObj === 'object') {

			this._id = metaObj.id;
			this._name = metaObj.name;
			this._src = metaObj.src;
			this._type = metaObj.type;
			this._isInternal = metaObj.isInternal;
			this._isLocked = metaObj.isLocked;
			this._aspectRatio = metaObj.aspectRatio;

			if (this._isInternal) {
				let r = t.objectStore(this._bank._binariesStoreName).get(this._id);
				r.onsuccess = (e) => {
					let blob = new Blob([e.target.result], {type: this._type});
					this._src = window.URL.createObjectURL(blob);
					this.makeCallback('change');
				};
				r.onerror = (e) => {
					console.error('Could not load an internal image\'s binary data. Error: '+e.target.errorCode);
				};
			}
		} else {
			console.error('The image\'s metadata not a valid object.');
			let savedId = this._id;
			this._init();
			this._id = savedId;
		}
	}

	_init() {
		this._id = uuidv4();
		this._name = '';
		this._src = '';
		this._type = '';
		this._isInternal = false;
		this._isLocked = false;
		this._aspectRatio = 1;
		this._storeMetadata();
	}

	_initWithDef(def) {
		this._id = def.id;
		this._name = def.name;
		this._src = def.src;
		this._type = def.type;
		this._isInternal = def.isInternal;
		this._isLocked = def.isLocked;
		this._aspectRatio = def.aspectRatio;
		this._storeMetadata();
	}

	_initWithFile(file) {
		this._id = uuidv4();
		this._name = file.name;
		this._src = window.URL.createObjectURL(file);
		this._type = file.type;
		this._isInternal = true;
		this._isLocked = false;
		this._aspectRatio = 1;
		this._storeMetadata();
		this._finishProcessingFile(file);
	}

	_getMetadata() {
		let dbObj = {};
		dbObj.id = this._id;
		dbObj.name = this._name;
		if (this._isInternal) {
			dbObj.src = '';
		} else {
			dbObj.src = this._src;
		}
		dbObj.type = this._type;
		dbObj.isInternal = this._isInternal;
		dbObj.isLocked = this._isLocked;
		dbObj.aspectRatio = this._aspectRatio;
		return dbObj;
	}

	_setDimensions(width, height) {
		this._aspectRatio = width / height;
		this._makeChangeCallback({aspectRatio: this._aspectRatio});
	}


	getId() {
		return this._id;
	}

	getName() {
		return this._name;
	}

	setName(arg) {
		this._name = arg;
		this._makeChangeCallback({name: this._name});
	}

	getSrc() {
		return this._src;
	}

	getIsLocked() {
		return this._isLocked;
	}

	getAspectRatio() {
		return this._aspectRatio;
	}

	
	getCanDelete() {
		return !(this._isLocked || this._retainCount > 0);
	}

	retain() {
		this._retainCount += 1;
		if (this._retainCount === 1) {
			this._makeChangeCallback({canDelete: false});
		}
	}

	release() {
		this._retainCount -= 1;
		if (this._retainCount < 0) {
			console.error('An image\'s retain count is less than zero.');
		}
		if (this._retainCount === 0) {
			this._makeChangeCallback({canDelete: this.getCanDelete()});
		}
	}


	/*
	**	Change
	*/

	_makeChangeCallback(data) {
		this._storeMetadata();
		this.makeCallback('change', data);
	}

	_storeMetadata() {
		if (this._bank._db === null) {
			return;
		}
		let dbObj = this._getMetadata();
		let t = this._bank._db.transaction(this._bank._metadataStoreName, 'readwrite');
		t.onabort = (e) => {
			console.error('The database transaction was aborted. Local storage space may not be available.');
		};
		let r = t.objectStore(this._bank._metadataStoreName).put(dbObj);
		r.onerror = (e) => {
			console.error('Failed to store object in database. Error: '+e.target.errorCode);
		};	
	}

	_finishProcessingFile(file) {

		// TODO: this part assumes files are always svg text xml
		let txtReader = new FileReader();
		txtReader.onload = () => {

			// Width and height will be values[3] and values[4].
			let viewBoxValuesRe = /viewBox\s*=\s*"\s*([0-9]*\.?[0-9]*)\s*,?\s*([0-9]*\.?[0-9]*)\s*,?\s*([0-9]*\.?[0-9]*)\s*,?\s*([0-9]*\.?[0-9]*)(?=\s*")/;
			let values = txtReader.result.match(viewBoxValuesRe);

			if (!Array.isArray(values)) {
				console.error('Failed to find the viewBox data in the SVG file.');
				return;
			}

			let width = parseFloat(values[3]);
			if (Number.isNaN(width) || !Number.isFinite(width) || width < 0) {
				console.error('Unable to determined the SVG file\'s width.');
				return;
			}

			let height = parseFloat(values[4]);
			if (Number.isNaN(height) || !Number.isFinite(height) || height < 0) {
				console.error('Unable to determined the SVG file\'s height.');
				return;
			}
	
			this._setDimensions(width, height);
 		};
		txtReader.onerror = () => {
			console.error('Error reading image file (as text). Error follows...');
			console.error(txtReader.error);
		};
		txtReader.readAsText(file);

		if (this._bank._db === null) {
			return;
		}

		let binReader = new FileReader();
		binReader.onload = () => {
			let t = this._bank._db.transaction(this._bank._binariesStoreName, 'readwrite');
			t.onabort = (e) => {
				console.error('The database transaction was aborted. Local storage space may not be available.');
			};
			let r = t.objectStore(this._bank._binariesStoreName).put(binReader.result, this._id);
			r.onerror = (e) => {
				console.error('Failed to store object in database. Error: '+e.target.errorCode);
			};	
		};
		binReader.onerror = (e) => {
			console.error('Error reading image file (as array buffer). Error follows...');
			console.error(binReader.error);
		};
		binReader.readAsArrayBuffer(file);

	}

	deleteFromDatabase() {
		if (this._bank._db === null) {
			return;
		}
		let t = this._bank._db.transaction([this._bank._binariesStoreName, this._bank._metadataStoreName], 'readwrite');
		let r1 = t.objectStore(this._bank._metadataStoreName).delete(this._id);
		r1.onerror = (e) => {
			console.error('Failed to delete an image\'s metadata from the database. Error: '+e.target.errorCode);
		};
		if (this._isInternal) {
			let r2 = t.objectStore(this._bank._binariesStoreName).delete(this._id);
			r2.onerror = (e) => {
				console.error('Failed to delete an image\'s binary data from the database. Error: '+e.target.errorCode);
			};
		}
	}

}

